import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Paper,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { BarChart, LineChart } from "@mui/x-charts";
import { AdminAPI } from "../api/admin";
import StatCard from "../components/StatCard";
import { format, parseISO } from "date-fns";

function formatDate(value) {
  if (!value) return "N/A";
  try {
    return format(parseISO(value), "MMM dd, yyyy h:mm a");
  } catch {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleString("en-IN");
  }
}

function formatDay(value) {
  if (!value) return "";
  try {
    return format(parseISO(value), "MMM d");
  } catch {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  }
}

export default function PartnerDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("assets_desc");
  const [snack, setSnack] = useState({ open: false, message: "" });
  const [data, setData] = useState({
    partner: null,
    enterprises: [],
    stats: {},
    charts: {
      asset_timeline: [],
      login_timeline: [],
      most_active_enterprises: [],
    },
  });

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await AdminAPI.getPartnerDashboard();
      setData(
        res?.data?.data || {
          partner: null,
          enterprises: [],
          stats: {},
          charts: {
            asset_timeline: [],
            login_timeline: [],
            most_active_enterprises: [],
          },
        }
      );
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load partner dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDashboard();
  }, []);

  const assetTimeline = useMemo(
    () =>
      (data.charts?.asset_timeline || []).map((row) => ({
        day: formatDay(row.day),
        total: Number(row.total || 0),
      })),
    [data.charts?.asset_timeline]
  );

  const loginTimeline = useMemo(
    () =>
      (data.charts?.login_timeline || []).map((row) => ({
        day: formatDay(row.day),
        total: Number(row.total || 0),
      })),
    [data.charts?.login_timeline]
  );

  const activeEnterprises = useMemo(
    () => data.charts?.most_active_enterprises || [],
    [data.charts?.most_active_enterprises]
  );

  const registerLink = useMemo(() => {
    const code = data.partner?.referralCode;
    if (!code || typeof window === "undefined") return "";
    return `${window.location.origin}/sign-up?ref=${encodeURIComponent(code)}`;
  }, [data.partner?.referralCode]);

  const sortedEnterprises = useMemo(() => {
    const items = [...(data.enterprises || [])];
    if (sortBy === "assets_desc") {
      return items.sort((a, b) => Number(b.assets_added || 0) - Number(a.assets_added || 0));
    }
    if (sortBy === "assets_asc") {
      return items.sort((a, b) => Number(a.assets_added || 0) - Number(b.assets_added || 0));
    }
    if (sortBy === "login_desc") {
      return items.sort((a, b) => new Date(b.last_login_at || 0) - new Date(a.last_login_at || 0));
    }
    if (sortBy === "name_asc") {
      return items.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    }
    return items;
  }, [data.enterprises, sortBy]);

  const copyToClipboard = async (value, message = "Copied to clipboard") => {
    await navigator.clipboard.writeText(String(value || ""));
    setSnack({ open: true, message });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>Loading partner dashboard...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", px: { xs: 1.5, md: 2 }, py: { xs: 1.5, md: 2.5 }, bgcolor: "#f8fafc" }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 3,
          borderRadius: 2,
          borderColor: "#dbe6f3",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 900, letterSpacing: 0.2, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
            >
              Partner Dashboard
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              View enterprise growth, login activity, and asset additions across your portfolio.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
              Last updated: {new Date().toLocaleString("en-IN", { hour12: false })}
            </Typography>
            <IconButton onClick={loadDashboard} size="small" sx={{ border: "1px solid #dbe6f3", borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Number of Enterprises"
            value={data.stats?.total_enterprises || 0}
            icon={<BusinessIcon color="primary" fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Active in Last 24 Hours"
            value={data.stats?.active_24h || 0}
            icon={<AccessTimeIcon color="success" fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Number of Assets Added"
            value={data.stats?.total_assets_added || 0}
            icon={<Inventory2Icon color="primary" fontSize="large" />}
          />
        </Grid>
      </Grid>

      {registerLink && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
            borderColor: "#dbe6f3",
            bgcolor: "#fff",
            mb: 4,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Share Register Link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy this onboarding link and share it with companies you want to register under your partner account.
          </Typography>
          <TextField
            fullWidth
            value={registerLink}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => copyToClipboard(registerLink)} edge="end">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      )}

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800, fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" } }}>
              Daily Assets Added
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block" mb={2}>
              Daily asset creation across all partner enterprises.
            </Typography>
            {assetTimeline.length > 0 ? (
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <LineChart
                  xAxis={[{ scaleType: "point", data: assetTimeline.map((d) => d.day) }]}
                  series={[{ data: assetTimeline.map((d) => d.total), label: "Assets Added", area: true, color: "#054693" }]}
                  height={300}
                  margin={{ top: 20, bottom: 40, left: 55, right: 20 }}
                  sx={{ minWidth: { xs: 420, sm: "100%" } }}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography variant="body2" color="text.secondary">
                  No asset activity available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800, fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" } }}>
              Daily Login Activity
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block" mb={2}>
              Login activity from enterprise administrators under your partner portfolio.
            </Typography>
            {loginTimeline.length > 0 ? (
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <LineChart
                  xAxis={[{ scaleType: "point", data: loginTimeline.map((d) => d.day) }]}
                  series={[{ data: loginTimeline.map((d) => d.total), label: "Logins", area: true, color: "#0f9d58" }]}
                  height={300}
                  margin={{ top: 20, bottom: 40, left: 55, right: 20 }}
                  sx={{ minWidth: { xs: 420, sm: "100%" } }}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography variant="body2" color="text.secondary">
                  No login activity available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800, fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" } }}>
              Most Active Enterprises
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block" mb={2}>
              Ranked by asset additions and recent admin activity.
            </Typography>
            {activeEnterprises.length > 0 ? (
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <BarChart
                  xAxis={[{ scaleType: "band", data: activeEnterprises.map((d) => d.name) }]}
                  series={[{ data: activeEnterprises.map((d) => d.activity_score), label: "Activity Score", color: "#054693" }]}
                  height={320}
                  margin={{ top: 20, bottom: 80, left: 55, right: 20 }}
                  sx={{ minWidth: { xs: 480, sm: "100%" } }}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={320}>
                <Typography variant="body2" color="text.secondary">
                  No enterprise activity available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: 2,
          borderColor: "#dbe6f3",
          bgcolor: "#fff",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 800, fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" } }}
        >
          Enterprise Portfolio
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 260 } }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="assets_desc">Assets added: high to low</MenuItem>
              <MenuItem value="assets_asc">Assets added: low to high</MenuItem>
              <MenuItem value="login_desc">Last login: newest</MenuItem>
              <MenuItem value="name_asc">Name: A to Z</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <TableContainer sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Email</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Assets Added</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Last Login</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sortedEnterprises || []).length > 0 ? (
                sortedEnterprises.map((enterprise) => (
                  <TableRow key={enterprise.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 700 }}>
                        {enterprise.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {enterprise.email || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={enterprise.assets_added || 0} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {formatDate(enterprise.last_login_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No enterprises assigned yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
            borderColor: "#dbe6f3",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <TrendingUpIcon sx={{ color: "#054693" }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Portfolio Notes
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            This dashboard is view-only for partner users. It shows enterprise growth, login activity, and asset creation trends
            without any edit actions so the data stays clean and focused for business review.
          </Typography>
        </Paper>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack({ open: false, message: "" })}>
        <Alert severity="success" onClose={() => setSnack({ open: false, message: "" })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
